import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../../services/enhancedApiClient";
import type { Lead, Contact } from "../types";

// Since types might not be fully defined in shared folder, defining partials here or assuming they exist
// Lead type is imported from ../types

class LeadsService {
    async getLeads(page = 1, limit = 10, search = "", status = ""): Promise<ApiResponse<{ leads: Lead[], pagination: any, summary?: any }>> {
        const params: any = { page, limit };
        if (search) params.search = search;
        if (status) params.status = status;

        return await enhancedApiClient.get("/leads", {
            params,
            context: "Get Leads",
            component: "LeadsService"
        });
    }

    async getLead(id: string): Promise<ApiResponse<Lead>> {
        return await enhancedApiClient.get(`/leads/${id}`, {
            context: "Get Lead",
            component: "LeadsService"
        });
    }

    async getLeadSources(): Promise<ApiResponse<string[]>> {
        return await enhancedApiClient.get("/leads/sources", {
            context: "Get Lead Sources",
            component: "LeadsService"
        });
    }

    async createLead(data: Partial<Lead> & { contactId?: string, name?: string, phone?: string, email?: string }): Promise<ApiResponse<Lead>> {
        return await enhancedApiClient.post("/leads", data, {
            context: "Create Lead",
            component: "LeadsService"
        });
    }

    async updateLead(id: string, data: Partial<Lead>): Promise<ApiResponse<Lead>> {
        return await enhancedApiClient.put(`/leads/${id}`, data, {
            context: "Update Lead",
            component: "LeadsService"
        });
    }

    async deleteLead(id: string): Promise<ApiResponse<void>> {
        return await enhancedApiClient.delete(`/leads/${id}`, {
            context: "Delete Lead",
            component: "LeadsService"
        });
    }

    async convertLead(id: string, data?: { contactPersonName?: string }): Promise<ApiResponse<any>> {
        return await enhancedApiClient.post(`/leads/${id}/convert`, data || {}, {
            context: "Convert Lead",
            component: "LeadsService"
        });
    }

    // Contacts
    async getContacts(page = 1, limit = 10, search = "", status = ""): Promise<ApiResponse<{ contacts: Contact[], pagination: any, summary?: any }>> {
        const params: any = { page, limit };
        if (search) params.search = search;
        if (status) params.status = status;
        return await enhancedApiClient.get("/contacts", {
            params,
            context: "Get Contacts",
            component: "LeadsService"
        });
    }

    async createContact(data: Partial<Contact>): Promise<ApiResponse<Contact>> {
        return await enhancedApiClient.post("/contacts", data, {
            context: "Create Contact",
            component: "LeadsService"
        });
    }

    async updateContact(id: string, data: Partial<Contact>): Promise<ApiResponse<Contact>> {
        return await enhancedApiClient.put(`/contacts/${id}`, data, {
            context: "Update Contact",
            component: "LeadsService"
        });
    }

    async deleteContact(id: string): Promise<ApiResponse<void>> {
        return await enhancedApiClient.delete(`/contacts/${id}`, {
            context: "Delete Contact",
            component: "LeadsService"
        });
    }

    async getSalesDashboard(): Promise<ApiResponse<any>> {
        return await enhancedApiClient.get("/dashboard/sales", {
            context: "Get Sales Dashboard",
            component: "LeadsService"
        });
    }
}

export const leadsService = new LeadsService();
